import { BOX_TYPE } from '@const/pageResponse.constants';
import { FourGraphBox, Page, PageResponseDTO, RegroupButtonBox, RegroupGraph, Tab } from '@dto/pageResponse.dto';
import { Button } from 'antd';
import axios, { AxiosError } from 'axios';
import { cloneDeep } from 'lodash-es';
import { createContext, FC, lazy, ReactElement, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { FaLayerGroup, FaUndo } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router';
import { RegroupedGraphBox, RegroupedGraphIntervalLabel } from 'src/component/graph/GraphTypes';
import { XmSpinner } from 'src/component/spinner/XmSpinner';
import XmTabsSticky from 'src/component/stickyTab/XmTabsSticky';
import BoxPage, { TabLabel } from 'src/content/basePage/BoxPage';
import { getTabKey, hashRecordToStr, parseHash } from 'src/routing/tabHash';
import { RoutePath } from 'src/data/Routes';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import { TextUtil } from 'src/util/TextUtil';
import { getApi } from 'src/util/apiCalls';
import './BasePage.less';

const UniversalGroups = lazy(() => import('src/content/configuration/device/serverGroups/UniversalGroups'));

interface BasePageProps {
  url: string;
}

type DeepReadonly<T> = {
  readonly [P in keyof T]: DeepReadonly<T[P]>
};

const TABS_DURATION: readonly DeepReadonly<Tab<RegroupedGraphBox>>[] = [
  { title: RegroupedGraphIntervalLabel.day, content: [{ graphs: [], interval: 'day', url: null, type: 'RegroupedGraphBox' }] },
  { title: RegroupedGraphIntervalLabel.week, content: [{ graphs: [], interval: 'week', url: null, type: 'RegroupedGraphBox' }] },
  { title: RegroupedGraphIntervalLabel.month, content: [{ graphs: [], interval: 'month', url: null, type: 'RegroupedGraphBox' }] },
  { title: RegroupedGraphIntervalLabel.year, content: [{ graphs: [], interval: 'year', url: null, type: 'RegroupedGraphBox' }] }
];


const IS_REGROUP_ACTIVE_DEFAULT = false as const;
export const TabsContext = createContext([] as Tab[]);
export const IsRegroupActive = createContext<boolean>(IS_REGROUP_ACTIVE_DEFAULT);

let lastNonDurationTabName = '';

export const BasePage: FC<BasePageProps> = (props) => {
  const [page, setPage] = useState<Page>();
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState(!props.url);
  const aborter = useRef<AbortController>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const [hash, setHash] = useState<{ subTab?: string; tab?: string; }>({});
  const isRegroupActiveRef = useRef<boolean>(IS_REGROUP_ACTIVE_DEFAULT); // updated only with tabs state
  const { current: isRegroupActive } = isRegroupActiveRef;

  const hashRef = useRef(hash);
  hashRef.current = hash;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const pageRef = useRef(page);
  pageRef.current = page;
  const locationRef = useRef(location);
  locationRef.current = location;
  const urlRef = useRef(props.url);
  urlRef.current = props.url;

  useEffect(() => {
    let lochash = hash;
    if (location.hash) {
      lochash = parseHash(location);
      setHash(lochash);
    }
    if (!aborter.current?.signal.aborted) {
      reloadData(lochash);
    }
  }, [props.url]);

  useEffect(() => {
    return () => {
      aborter.current?.abort();
    };
  }, []);

  function reloadData(lochash = hash) {
    setLoading(true);
    aborter.current?.abort();
    aborter.current = new AbortController();
    getApi<PageResponseDTO>(props.url, aborter.current?.signal).then(
      (response) => {
        const p = response.data.data;
        if (!p?.tabs?.length) {
          console.log('No tab', p);
        } else if (lochash.tab) {
          let found = p.tabs.find(t => getTabKey(t) === lochash.tab);
          if (!found && lastNonDurationTabName) {
            found = p.tabs.find(t => getTabKey(t) === lastNonDurationTabName);
            if (found) {
              lochash.tab = lastNonDurationTabName;
            }
          }
          if (found) {
            setActiveTab(lochash.tab, lochash);
          } else {
            setActiveTab(null);
          }
        } else {
          setActiveTab(null);
        }
        setPage(p);
        setTabs(p?.tabs);
        setTimeout(() => {
          setLoading(false);
        });
      },
      (reason: AxiosError) => {
        setLoading(false);
        if (axios.isCancel(reason)) return;
        Log.error('Failed to get tab page!', reason);
      }
    );
  }

  function isDurationTab(tab: string, loctabs = tabsRef.current) {
    return loctabs?.some(t => t.content?.some(c => c.type === 'FourGraphBox'))
      && TABS_DURATION.some(t => getTabKey(t) === tab);
  }

  function setActiveTab(tabKey: string, lochash = hashRef.current) {
    if (tabKey)
      lochash.tab = tabKey;
    else
      delete lochash.tab;

    const loc = locationRef.current;
    const hashStr = hashRecordToStr(lochash);
    navigate(decodeURIComponent(loc.pathname + loc.search) + hashStr, { replace: true });
    delete lochash.subTab;
    setHash({ ...lochash });
  }

  function getActiveTab(loctabs = tabsRef.current, lochash = hashRef.current) {
    if (lochash.tab)
      return loctabs.find(t => getTabKey(t) === lochash.tab) ?? loctabs[0];
    return loctabs[0];
  }

  function regroupTabs(loctabs = tabsRef.current, lochash = hashRef.current) {

    const durationTabs = cloneDeep(TABS_DURATION) as Tab<RegroupedGraphBox>[];
    const daily = durationTabs[0].content[0];
    const weekly = durationTabs[1].content[0];
    const monthly = durationTabs[2].content[0];
    const yearly = durationTabs[3].content[0];
    daily.url = weekly.url = monthly.url = yearly.url = urlRef.current;

    const selectedTab = getActiveTab(loctabs, lochash);
    const regroupBox = selectedTab.content.find(b => b.type === 'RegroupButtonBox') as RegroupButtonBox;
    if (regroupBox) {
      const regs: RegroupGraph[] = regroupBox.graphs;
      daily.graphs.push(...regs);
      weekly.graphs.push(...regs);
      monthly.graphs.push(...regs);
      yearly.graphs.push(...regs);
    }
    else {
      for (const tab of loctabs) {
        // Find FourGraphBox in content array
        const graphBox = tab?.content?.find(c => c.type === BOX_TYPE.FourGraphBox) as FourGraphBox;
        const hasIncompatibleBox = tab?.content?.some(c =>
          c.type === BOX_TYPE.TableBox ||
          c.type === BOX_TYPE.TableHeaderGroupBox ||
          c.type === BOX_TYPE.TableGroupBox ||
          c.type === BOX_TYPE.TablePivotBox
        );

        if (graphBox) {
          const reg = { ...graphBox, label: tab.title, tab: tab.title, url: urlRef.current } as RegroupGraph;
          daily.graphs.push(reg);
          weekly.graphs.push(reg);
          monthly.graphs.push(reg);
          yearly.graphs.push(reg);
        } else if (!hasIncompatibleBox) {
          (durationTabs as Tab[]).push(tab);
        }
        // Skip incompatible tabs (TableBox, TableHeaderGroupBox, etc.)
      }
    }

    if (!isDurationTab(lochash.tab, loctabs)) {
      lastNonDurationTabName = lochash.tab;
      setActiveTab(getTabKey(durationTabs[0]));
    }

    setTabs(durationTabs);
    isRegroupActiveRef.current = true;
  }

  function regroupTabsBack() {
    setActiveTab(lastNonDurationTabName || getTabKey(pageRef.current.tabs[0]));
    setTabs(pageRef.current.tabs);
    isRegroupActiveRef.current = false;
  }

  // Pages whose content is sourced from another page type via a non-standard route (e.g. the
  // Self Monitoring settings page reusing LinuxServerPage.getPage()) set `menuContext` so
  // PerformanceBox sidebar filtering can resolve the right hw_type/type/item_id even though
  // props.url doesn't follow the generic /page/{hw_type}/{type} pattern.
  const boxUrl = page?.menuContext
    ? `/api/menu/v1/page/${page.menuContext.hw_type}/${page.menuContext.type}?${new URLSearchParams({
      ...(page.menuContext.item_id ? { item_id: page.menuContext.item_id } : {}),
      ...(page.menuContext.group_id ? { group_id: page.menuContext.group_id } : {}),
    }).toString()}`
    : props.url;

  const tabItems = useMemo(
    () => tabs.map(t => ({
      key: getTabKey(t),
      label: <TabLabel label={t.title} hash={{ tab: getTabKey(t) }} />,
      className: 'xm-base-tab',
      children: <>
        {(t.content?.some(c => c.type === 'RegroupButtonBox') || t.content?.some(c => c.type === 'FourGraphBox'))
          && <Button className='xm-regroup-btn' type='text' title="Regroup tabs by graph's time ranges" onClick={() => regroupTabs()}><FaLayerGroup /></Button>}
        {t.content?.some(c => c.type === 'RegroupedGraphBox')
          && <Button className='xm-regroup-btn' type='text' title="Regroup tabs back by metric" onClick={regroupTabsBack}><FaUndo /></Button>}
        <Suspense fallback={<XmSpinner />}>
          <IsRegroupActive.Provider value={isRegroupActive}>
            <BoxPage tab={t} url={boxUrl} />
          </IsRegroupActive.Provider>
        </Suspense>
      </>
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabs, isRegroupActive, boxUrl]
  );

  if (page?.formPage) {
    return <Suspense fallback={<XmSpinner />}>
      <UniversalGroups label={page.formPage.title} hwTypes={page.formPage.hwTypes} />
    </Suspense>;
  }

  // resolved key (hash or first tab) so the sticky tabs mark the visible pane for nested sub tabs
  const activeTabKey = tabs.length ? getTabKey(getActiveTab()) : undefined;

  return <div className='xm-base-page'>{loading ? (
      <XmSpinner delay={100} />
    ) : page && !page.tabs ? (
      <XmSpinner delay={1000} description={'No tab! ' + TextUtil.formatJSON(page)} />
    ) : !page?.tabs?.length ? <Delayed>
      <div className='xm-base-single'>
        There is no data available for this page.&#32;
        {props.url.includes("powervm") ? <span><br></br>To see performance data for this Power VM, install our OS Agent on this VM from: <a target="_blank" href='https://xormon.com/install-agent.php'>https://xormon.com/</a></span> : ""}
        {GLOB.selectedClass === RoutePath.CUSTOM_GROUPS ? 'Please check definition of your custom group.' : ''}
      </div>
    </Delayed>
      : tabs.length > 1 ? (
        <TabsContext.Provider value={tabs}>
          <XmTabsSticky activeKey={activeTabKey}
            onChange={setActiveTab}
            className="xm-base-tabs"
            items={tabItems} />
        </TabsContext.Provider>
      ) : page.tabs[0].content.some(c => c.type === BOX_TYPE.TopologyRequestBox) ? <Suspense fallback={<XmSpinner />}><BoxPage tab={page.tabs[0]} url={boxUrl} /></Suspense>
        : <div className='xm-base-single'>
          {tabs.filter(t => t.content?.some(c => c.type === 'RegroupButtonBox')).length >= 1
            && <Button className='xm-regroup-btn' type='text' title="Regroup tabs by graph's time ranges" onClick={e => regroupTabs()}><FaLayerGroup /></Button>}
          {tabs.some(t => t.content?.some(c => c.type === 'RegroupedGraphBox'))
            && <Button className='xm-regroup-btn' type='text' title="Regroup tabs back by metric" onClick={regroupTabsBack}><FaUndo /></Button>}
          <Suspense fallback={<XmSpinner />}><BoxPage tab={page.tabs[0]} url={boxUrl} /></Suspense>
        </div>
    }</div>;
};

interface DelayedProps {
  /**
   * Nodes to render after timeout
   */
  children: React.ReactNode;
  /**
   * Timeout in ms
   */
  waitBeforeShow?: number;
  /**
   * Element to show while waiting
   */
  placeholder?: ReactElement;
}

export const Delayed: FC<DelayedProps> = ({ children, waitBeforeShow = 500, placeholder = null }) => {
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setIsShown(true);
    }, waitBeforeShow);
    return () => window.clearTimeout(timer);
  }, [waitBeforeShow]);

  return isShown ? children : placeholder;
};